// ═══════════════════════════════════════════════════════════════
// JobLeaveModal — the "wrap up" prompt that fires when the mechanic
// taps Leave on a started job. Branches into two flows:
//
//   1. "Yes, complete" → close-out form (what you did, extra parts,
//      optional Mark Paid for cash deals) → if not paid, prompt to
//      Send Invoice via Skip / Text / Email (preferred channel
//      highlighted orange, same pattern as Jobs.jsx send buttons)
//
//   2. "No, coming back" → free-text note + "Waiting on a part?"
//      toggle + date+time picker. On submit, appends a new entry
//      to job.returnVisits[] (existing scheduledAt entry stays
//      put — return visits live alongside it, NOT instead of it).
//      Default duration is 30 min ("most of the time the first
//      visit is longer" — Brad).
//
// All Firestore writes happen here; the parent Jobs.jsx just
// hands us the job + customer + businessProfile + an onClose
// callback. We stay open through invoice sending (which hands
// off via mailto:/sms: → window.location.href, so the modal
// closes itself before the channel fires).
// ═══════════════════════════════════════════════════════════════
import React, { useMemo, useState } from 'react';
import {
  serverTimestamp, Timestamp, addDoc, collection,
} from 'firebase/firestore';
import {
  X, Check, ArrowLeft, ClipboardCheck, RotateCcw, Package,
  CalendarPlus, MessageSquare, Mail, DollarSign,
} from 'lucide-react';
import { db, auth } from './firebase.js';
import { jobsCol } from './database.js';
import { updateOne } from './lib/db-crud.js';
import { useUserData } from './lib/use-user-data.js';
import {
  randomToken, shortId, openSms, openMailto, toDate, flatMapVisits,
} from './lib/job-helpers.js';
import { businessSnapshot } from './lib/use-business-profile.js';
import { buildJobPhotosSnapshot, readInspectionSnapshot } from './lib/share-snapshot.js';
import { hasJobNumber } from './lib/job-number.js';

const RETURN_DEFAULT_DURATION = 30;

// Default the date picker to "tomorrow at the same time of day as the
// original visit" — almost always what Brad wants. Fall back to
// "tomorrow at 9am" if the original had no time-of-day.
function defaultReturnDateTime(job) {
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const orig = toDate(job.scheduledAt);
  if (orig) {
    tomorrow.setHours(orig.getHours(), orig.getMinutes(), 0, 0);
  } else {
    tomorrow.setHours(9, 0, 0, 0);
  }
  return tomorrow;
}

function pad2(n) { return String(n).padStart(2, '0'); }
function dateInputValue(d) {
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}
function timeInputValue(d) {
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}
function combineDateAndTime(dateStr, timeStr) {
  const [y, mo, da] = dateStr.split('-').map(Number);
  const [h, mi]    = timeStr.split(':').map(Number);
  return new Date(y, mo - 1, da, h, mi, 0, 0);
}
function formatTimeOfDay(d) {
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
function formatDayLabel(d) {
  return d.toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' });
}

export default function JobLeaveModal({ job, customer, businessProfile, onClose }) {
  // Three-screen state machine
  // 'ask'      — Is the job complete?
  // 'closeOut' — close-out form (work summary, extra parts, mark paid)
  // 'sendInvoice' — channel choice after closing (skipped if already paid)
  // 'return'   — schedule return visit
  const [step, setStep] = useState('ask');

  return (
    <div
      className="modal-overlay"
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div className="modal-box leave-modal" role="dialog" aria-modal="true">
        {step === 'ask' && (
          <AskStep
            job={job}
            customer={customer}
            onClose={onClose}
            onYes={() => setStep('closeOut')}
            onNo={() => setStep('return')}
          />
        )}
        {step === 'closeOut' && (
          <CloseOutStep
            job={job}
            customer={customer}
            onBack={() => setStep('ask')}
            onClose={onClose}
            onClosedUnpaid={() => setStep('sendInvoice')}
          />
        )}
        {step === 'sendInvoice' && (
          <SendInvoiceStep
            job={job}
            customer={customer}
            businessProfile={businessProfile}
            onClose={onClose}
          />
        )}
        {step === 'return' && (
          <ScheduleReturnStep
            job={job}
            customer={customer}
            onBack={() => setStep('ask')}
            onClose={onClose}
          />
        )}
      </div>
    </div>
  );
}

// ─── Step 1: ask if the job is complete ──────────────────────────
function AskStep({ job, customer, onClose, onYes, onNo }) {
  return (
    <>
      <div className="modal-header">
        <h2>Wrapping up</h2>
        <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <p className="muted small leave-modal-sub">
        {customer ? `On the job for ${customer.name}.` : null}
        {' '}Is the work fully complete, or are you coming back?
      </p>
      <div className="leave-choice-grid">
        <button type="button" className="leave-choice complete" onClick={onYes}>
          <ClipboardCheck size={28} />
          <span className="leave-choice-title">Yes, all done</span>
          <span className="leave-choice-sub">Close out and (optionally) send the invoice</span>
        </button>
        <button type="button" className="leave-choice return" onClick={onNo}>
          <RotateCcw size={28} />
          <span className="leave-choice-title">No, coming back</span>
          <span className="leave-choice-sub">Schedule the return visit</span>
        </button>
      </div>
    </>
  );
}

// ─── Step 2A: close-out form ─────────────────────────────────────
function CloseOutStep({ job, customer, onBack, onClose, onClosedUnpaid }) {
  const [workSummary, setWorkSummary] = useState(job.workSummary || '');
  const [extraParts,  setExtraParts]  = useState(job.extraParts  || '');
  const [markPaid,    setMarkPaid]    = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState(null);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);
    setSubmitting(true);
    try {
      // Timestamp.now() (not serverTimestamp) so the local cache reflects
      // the new closed/paid state immediately — closed jobs disappear
      // from active lists on the same tick rather than after the server
      // round-trip.
      const patch = {
        workSummary: workSummary.trim(),
        extraParts:  extraParts.trim(),
        closedAt:    Timestamp.now(),
        // Clear any pending return state from a prior Leave-not-complete
        awaitingReturn: false,
        awaitingPart:   false,
      };
      // Capture rough work duration if startedAt is set (latest session only;
      // multi-session tracking = v0.2 per spec).
      const start = toDate(job.startedAt);
      if (start) {
        patch.workMinutes = Math.max(0, Math.round((Date.now() - start.getTime()) / 60_000));
      }
      if (markPaid) {
        patch.status = 'paid';
        patch.paidAt = Timestamp.now();
      }
      await updateOne(jobsCol(), job.id, patch);

      if (markPaid) {
        // Cash deal — done. No invoice prompt.
        onClose();
      } else {
        // Hand off to the Send Invoice prompt step
        onClosedUnpaid();
      }
    } catch (err) {
      console.error('Close-out failed:', err);
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="modal-header">
        <button type="button" className="btn-link" onClick={onBack} aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <h2 style={{ flex: 1, textAlign: 'center', margin: 0 }}>Close out</h2>
        <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="form">
        <label className="form-row">
          <span>What you did</span>
          <textarea
            rows={3} maxLength={1000} autoFocus
            value={workSummary}
            onChange={(e) => setWorkSummary(e.target.value)}
            placeholder="e.g., Replaced battery + cleaned terminals. Also tightened serpentine belt."
          />
        </label>
        <label className="form-row">
          <span>Extra parts not in the estimate</span>
          <textarea
            rows={2} maxLength={500}
            value={extraParts}
            onChange={(e) => setExtraParts(e.target.value)}
            placeholder="e.g., Belt tensioner pulley — $42"
          />
        </label>
        <label className="checkbox-row" style={{ padding: '6px 0' }}>
          <input
            type="checkbox"
            checked={markPaid}
            onChange={(e) => setMarkPaid(e.target.checked)}
          />
          <span>
            <DollarSign size={14} style={{ verticalAlign: '-2px', marginRight: 4, color: 'var(--accent)' }} />
            Customer paid now (cash, Venmo, etc.)
          </span>
        </label>
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="btn-link" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn" disabled={submitting}>
            <Check size={16} strokeWidth={2.5} />
            {submitting ? 'Closing…' : 'Close job'}
          </button>
        </div>
      </form>
    </>
  );
}

// ─── Step 3: Send invoice prompt (only if not Mark-Paid'd) ───────
function SendInvoiceStep({ job, customer, businessProfile, onClose }) {
  const [submitting, setSubmitting] = useState(null); // null | 'text' | 'email'
  const [error, setError] = useState(null);

  const hasEmail     = !!customer?.email;
  const prefersText  = customer?.preferredContact?.includes('text');
  const prefersEmail = customer?.preferredContact?.includes('email');

  async function handleSend(channel) {
    if (channel === 'email' && !hasEmail) return;
    setSubmitting(channel);
    setError(null);
    try {
      const paymentToken = randomToken();
      const jobPhotos = buildJobPhotosSnapshot(job);
      const inspectionReport = await readInspectionSnapshot(job.id);
      const invRef = await addDoc(collection(db, 'sharedInvoices'), {
        userId: auth.currentUser.uid,
        jobId: job.id,
        customerName: customer?.name || '',
        description: job.description,
        totalPrice: job.totalPrice,
        ...(hasJobNumber(job) ? { jobNumber: job.jobNumber } : {}),
        lineItems: Array.isArray(job.lineItems) ? job.lineItems : [],
        shopSupplies: Number(job.shopSupplies) || 0,
        ...businessSnapshot(businessProfile),
        ...(jobPhotos ? { jobPhotos } : {}),
        ...(inspectionReport ? { inspectionReport } : {}),
        paymentToken,
        paid: false,
        paidAt: null,
        createdAt: serverTimestamp(),
      });
      await updateOne(jobsCol(), job.id, {
        invoiceShareId: invRef.id,
        status: 'invoiced',
        invoicedAt: serverTimestamp(),
      });
      const invoiceUrl = `${window.location.origin}/invoice/${invRef.id}`;
      const body = `Invoice from your mechanic:\n\n${invoiceUrl}`;
      // Close the modal first, then fire the channel — channel openers do
      // window.location.href which would cancel any in-flight close.
      onClose();
      if (channel === 'email') {
        openMailto(customer.email, 'Invoice from your mechanic', body);
      } else {
        openSms(customer?.phone, body);
      }
    } catch (err) {
      console.error('Send invoice failed:', err);
      setError(err.message);
      setSubmitting(null);
    }
  }

  return (
    <>
      <div className="modal-header">
        <h2>Send invoice now?</h2>
        <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <p className="muted small leave-modal-sub">
        Job is closed. Send the invoice while you're standing here, or skip and send later from the customer page.
      </p>
      <div className="leave-send-grid">
        <button
          type="button"
          className="btn-secondary leave-send-btn"
          onClick={onClose}
          disabled={!!submitting}
        >
          Skip — send later
        </button>
        <button
          type="button"
          className={`btn leave-send-btn ${prefersText ? 'preferred' : ''}`}
          onClick={() => handleSend('text')}
          disabled={!!submitting}
          title={prefersText ? 'Customer prefers text' : ''}
        >
          <MessageSquare size={16} />
          {submitting === 'text' ? 'Sending…' : 'Text Invoice'}
          {prefersText && <span className="leave-pref-tag">preferred</span>}
        </button>
        {hasEmail && (
          <button
            type="button"
            className={`btn leave-send-btn ${prefersEmail ? 'preferred' : ''}`}
            onClick={() => handleSend('email')}
            disabled={!!submitting}
            title={prefersEmail ? 'Customer prefers email' : ''}
          >
            <Mail size={16} />
            {submitting === 'email' ? 'Sending…' : 'Email Invoice'}
            {prefersEmail && <span className="leave-pref-tag">preferred</span>}
          </button>
        )}
      </div>
      {error && <p className="form-error" style={{ marginTop: 12 }}>{error}</p>}
    </>
  );
}

// ─── Step 2B: schedule a return visit ────────────────────────────
function ScheduleReturnStep({ job, customer, onBack, onClose }) {
  // Live jobs subscription so we can check the proposed return slot
  // against every other appointment + return visit on the calendar.
  const { jobs: allJobs } = useUserData();

  const initialReturn = useMemo(() => defaultReturnDateTime(job), [job.id]);
  const [returnDate, setReturnDate] = useState(dateInputValue(initialReturn));
  const [returnTime, setReturnTime] = useState(timeInputValue(initialReturn));
  const [duration,   setDuration]   = useState(String(RETURN_DEFAULT_DURATION));
  const [returnNotes, setReturnNotes] = useState('');
  const [waitingPart, setWaitingPart] = useState(false);
  const [submitting,  setSubmitting]  = useState(false);
  const [error,       setError]       = useState(null);

  // Live preview of any existing block this slot would overlap.
  // Recomputed as you change date/time/duration so you see the warning
  // before tapping Schedule.
  const conflictPreview = useMemo(() => {
    const scheduledAt = combineDateAndTime(returnDate, returnTime);
    const dur = Number(duration);
    if (isNaN(scheduledAt.getTime()) || !isFinite(dur) || dur < 1) return null;
    const end = new Date(scheduledAt.getTime() + dur * 60_000);
    const blocks = flatMapVisits(allJobs);
    return blocks.find((b) => {
      const bEnd = new Date(b._start.getTime() + b._durationMinutes * 60_000);
      return scheduledAt < bEnd && end > b._start;
    }) || null;
  }, [allJobs, returnDate, returnTime, duration]);

  async function handleSubmit(e) {
    e.preventDefault();
    setError(null);

    const dur = Number(duration);
    if (!isFinite(dur) || dur < 15) { setError('Duration must be at least 15 minutes.'); return; }
    const scheduledAt = combineDateAndTime(returnDate, returnTime);
    if (isNaN(scheduledAt.getTime())) { setError('Pick a valid date and time.'); return; }

    // Conflict check — block at submit time so a fresh conflict appearing
    // after you opened the picker still gets caught. Allow override.
    if (conflictPreview) {
      const cName = conflictPreview.customerId === job.customerId
        ? '(this same job — different visit)'
        : `another customer at ${formatTimeOfDay(conflictPreview._start)}`;
      const ok = confirm(
        `Heads up — this overlaps with ${cName}.\n\n`
        + `OK to schedule anyway. Cancel to pick a different time.`
      );
      if (!ok) return;
    }

    setSubmitting(true);
    try {
      const newVisit = {
        id:               shortId(),
        scheduledAt,                           // plain Date — Firestore converts to Timestamp
        durationMinutes:  dur,
        kind:             waitingPart ? 'parts-arrival' : 'return',
        notes:            returnNotes.trim(),
        addedAt:          new Date(),
      };
      // Append to existing returnVisits[] if present, else start a new array.
      const existing = Array.isArray(job.returnVisits) ? job.returnVisits : [];
      const patch = {
        returnVisits:   [...existing, newVisit],
        awaitingReturn: true,
        awaitingPart:   waitingPart,
        returnNotes:    returnNotes.trim(),
        // Capture session time and clear the in-progress flag.
        // Don't set closedAt — the job is NOT closed.
      };
      const start = toDate(job.startedAt);
      if (start) {
        // Latest-session work minutes — accumulated across multi-session
        // is v0.2 per spec. For now this gets overwritten next time.
        patch.workMinutes = Math.max(0, Math.round((Date.now() - start.getTime()) / 60_000));
      }
      // Clear startedAt so Start can re-arm when Brad returns.
      patch.startedAt = null;

      await updateOne(jobsCol(), job.id, patch);
      onClose();
    } catch (err) {
      console.error('Schedule return failed:', err);
      setError(err.message);
      setSubmitting(false);
    }
  }

  return (
    <>
      <div className="modal-header">
        <button type="button" className="btn-link" onClick={onBack} aria-label="Back">
          <ArrowLeft size={18} />
        </button>
        <h2 style={{ flex: 1, textAlign: 'center', margin: 0 }}>Coming back</h2>
        <button type="button" className="btn-link" onClick={onClose} aria-label="Close">
          <X size={18} />
        </button>
      </div>
      <form onSubmit={handleSubmit} className="form">
        <label className="form-row">
          <span>What you did so far (optional)</span>
          <textarea
            rows={2} maxLength={1000} autoFocus
            value={returnNotes}
            onChange={(e) => setReturnNotes(e.target.value)}
            placeholder="e.g., Diagnosed bad alternator. Need to get one and come back."
          />
        </label>
        <label className="checkbox-row" style={{ padding: '6px 0' }}>
          <input
            type="checkbox"
            checked={waitingPart}
            onChange={(e) => setWaitingPart(e.target.checked)}
          />
          <span>
            <Package size={14} style={{ verticalAlign: '-2px', marginRight: 4, color: 'var(--accent)' }} />
            Waiting on a part to arrive
          </span>
        </label>
        <div className="form-row-pair">
          <label className="form-row">
            <span>Return date</span>
            <input
              type="date" required
              value={returnDate}
              onChange={(e) => setReturnDate(e.target.value)}
            />
          </label>
          <label className="form-row">
            <span>Time</span>
            <input
              type="time" required step="900"
              value={returnTime}
              onChange={(e) => setReturnTime(e.target.value)}
            />
          </label>
          <label className="form-row">
            <span>Duration (min)</span>
            <input
              type="number" required min={15} step={15} inputMode="numeric"
              value={duration}
              onChange={(e) => setDuration(e.target.value)}
            />
          </label>
        </div>
        {/* Live conflict preview — recomputes as you change date/time.
            Same submit-time confirm() runs even if you ignore this. */}
        {conflictPreview && (
          <p className="conflict-warning">
            ⚠ This slot overlaps with{' '}
            {conflictPreview.customerId === job.customerId
              ? <>another visit on this same job</>
              : <><strong>{conflictPreview.description || 'another job'}</strong> at {formatTimeOfDay(conflictPreview._start)} on {formatDayLabel(conflictPreview._start)}</>
            }. You can still schedule it — you'll get a confirm prompt.
          </p>
        )}
        {error && <p className="form-error">{error}</p>}
        <div className="form-actions">
          <button type="button" className="btn-link" onClick={onClose}>Cancel</button>
          <button type="submit" className="btn" disabled={submitting}>
            <CalendarPlus size={16} strokeWidth={2.5} />
            {submitting ? 'Scheduling…' : 'Schedule return'}
          </button>
        </div>
      </form>
    </>
  );
}
